import { Box, Container, Grid, Typography, Stack, Button, Divider } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { NAVY_DARK, GREEN, GREEN_DARK, GREEN_LIGHT, PHONE, PHONE_DIGITS } from '../theme';
import { formatINR, computePlanDetails } from '../roiCalc';
import logo from '../logo-white.png';

const snapshot = computePlanDetails(1); // 1 Unit · ₹99,000 Starter Plan

export default function Footer() {
  return (
    <Box component="footer" sx={{
      bgcolor: NAVY_DARK, color: 'rgba(255,255,255,0.75)',
      pt: { xs: 6, md: 8 }, pb: 3,
    }}>
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ mb: 4 }}>

          {/* Brand */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box component="img" src={logo} alt="RideX City"
              sx={{ height: 46, objectFit: 'contain', mb: 2.5, display: 'block' }} />
            <Typography variant="body2" sx={{
              color: 'rgba(255,255,255,0.62)',
              lineHeight: 1.8, maxWidth: 300, mb: 3
            }}>
              Electric vehicle rentals for the everyday city commute.
              Invest smart. Earn monthly.
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button variant="contained" size="small" href={`tel:${PHONE}`} startIcon={<PhoneIcon />}
                sx={{ bgcolor: GREEN, color: '#fff', '&:hover': { bgcolor: GREEN_DARK }, fontWeight: 700 }}>
                {PHONE}
              </Button>
              <Button variant="outlined" size="small"
                href={`https://wa.me/${PHONE_DIGITS}?text=Hi%2C%20interested%20in%20RideX%20City%20investment`}
                target="_blank" rel="noopener noreferrer" startIcon={<WhatsAppIcon />}
                sx={{
                  color: '#fff', borderColor: 'rgba(255,255,255,0.28)',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.06)' }
                }}>
                WhatsApp
              </Button>
            </Stack>
          </Grid>

          {/* Quick links */}
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, mb: 2.5, letterSpacing: 0.5 }}>
              INVEST
            </Typography>
            <Stack spacing={1.5}>
              {[
                ['Why RideX', '/#why'],
                ['Cost Breakdown', '/#cost-breakdown'],
                ['ROI Plans', '/#roi-plans'],
                ['Contact Us', '/#contact'],
              ].map(([label, href]) => (
                <Typography key={label} variant="body2" component="a" href={href}
                  sx={{ color: 'rgba(255,255,255,0.62)', textDecoration: 'none', '&:hover': { color: GREEN_LIGHT }, transition: 'color 0.2s' }}>
                  {label}
                </Typography>
              ))}
            </Stack>
          </Grid>

          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, mb: 2.5, letterSpacing: 0.5 }}>
              LEGAL & POLICIES
            </Typography>
            <Stack spacing={1.5}>
              {[
                ['Privacy Policy', '/privacy-policy'],
                ['Terms & Conditions', '/terms-and-conditions'],
                ['Fleet Partners', '/#why'],
                ['Investor FAQ', '/#roi-plans'],
              ].map(([label, href]) => (
                <Typography key={label} variant="body2" component={Link} to={href}
                  sx={{
                    color: 'rgba(255,255,255,0.62)', textDecoration: 'none',
                    '&:hover': { color: GREEN_LIGHT }, transition: 'color 0.2s'
                  }}>
                  {label}
                </Typography>
              ))}
            </Stack>
          </Grid>

          {/* Plan summary box */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{
              p: 3, borderRadius: 3, border: `1px solid ${alpha(GREEN, 0.35)}`,
              bgcolor: 'rgba(121,171,29,0.08)'
            }}>
              <Typography variant="subtitle2" sx={{ color: GREEN_LIGHT, fontWeight: 700, mb: 2, letterSpacing: 0.5 }}>
                PLAN 1 SNAPSHOT · {formatINR(snapshot.totalInvestment)} (1 UNIT)
              </Typography>
              {[
                ['Investment', formatINR(snapshot.totalInvestment)],
                ['Monthly Payout', `${formatINR(snapshot.monthlyReturn)} / mo`],
                ['Annual Return', `${formatINR(snapshot.annualReturn)} / yr`],
                ['Annual ROI', `${snapshot.annualRoiPct.toFixed(1)}% p.a.`],
              ].map(([k, v]) => (
                <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.2 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>{k}</Typography>
                  <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700, fontFamily: '"Space Mono",monospace' }}>{v}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', mb: 3 }} />
        <Box sx={{
          display: 'flex', flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', alignItems: { xs: 'center', md: 'center' }, gap: 2,
          textAlign: { xs: 'center', md: 'left' }
        }}>
          <Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, mb: 0.4 }}>
              © 2026 RideXCity. All Rights Reserved.
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', lineHeight: 1.6 }}>
              A product of VALUEAID INFRASTRUCTURE PRIVATE LIMITED. &nbsp;|&nbsp; Made in India 🇮🇳.
            </Typography>
          </Box>

          <Stack direction="row" spacing={2.5} alignItems="center">
            <Typography component={Link} to="/privacy-policy" variant="caption"
              sx={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', '&:hover': { color: GREEN_LIGHT } }}>
              Privacy Policy
            </Typography>
            <Typography component={Link} to="/terms-and-conditions" variant="caption"
              sx={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', '&:hover': { color: GREEN_LIGHT } }}>
              Terms & Conditions
            </Typography>
            <Typography component="a" href="https://www.ridexcity.com" target="_blank" rel="noopener noreferrer"
              variant="body2" sx={{ color: GREEN_LIGHT, textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
              www.ridexcity.com
            </Typography>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
